// 文件编码: UTF-8
// 说明：本文件包含中文注释，需确保以 UTF-8 编码保存
import { AvatarDropdown, AvatarName, Footer, Question, SelectLang } from '@/components';
import { currentUser as queryCurrentUser } from '@/services/auth';
import { getLanguages } from '@/services/settings';
import { LinkOutlined, UserOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import { StrictMode } from 'react';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
	settings?: Partial<LayoutSettings>;
	currentUser?: API.CurrentUser;
	loading?: boolean;
	fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
	languages?: { value: string; label: string; flag?: string }[];
}> {
	const fetchUserInfo = async () => {
		try {
			// 直接尝试获取用户信息，依赖 Cookie 进行认证
			const response = await queryCurrentUser();

			// 后端直接返回用户数据
			if (response) {
				return response;
			}

			// 如果没有用户数据，跳转到登录页
			if (history.location.pathname !== loginPath) {
				history.push(loginPath);
			}
			return undefined;
		} catch (error: unknown) {
			const status = (error as any)?.response?.status;
			// 仅在开发环境且非 401/403 错误时打印
			if (isDev && status !== 401 && status !== 403) {
				console.error('获取用户信息失败:', error);
			}
			// 只有在确实是认证错误时才跳转
			if (status === 401 || status === 403) {
				if (history.location.pathname !== loginPath) {
					history.push(loginPath);
				}
			}
		}
		return undefined;
	};
	// 如果不是登录页面，执行
	const { location } = history;

	if (![loginPath, '/user/register', '/user/register-result'].includes(location.pathname)) {
		const currentUser = await fetchUserInfo();
		let languages: { value: string; label: string; flag?: string }[] = [];

		if (currentUser) {
			try {
				const langRes = await getLanguages();
				if (langRes.success && langRes.data) {
					const langList = Array.isArray(langRes.data)
						? langRes.data
						: langRes.data.languages || [];

					if (Array.isArray(langList)) {
						languages = langList.map((l: any) => {
							let value = l.locale || l.code;
							if (value === 'zh-CN') {
								value = 'zh';
							} else if (value === 'en-US') {
								value = 'en';
							}
							return {
								value,
								label: l.name,
								flag: l.flag
							};
						});
					}
				}
			} catch (e) {
				console.error('Failed to fetch languages:', e);
			}
		}

		return {
			fetchUserInfo,
			currentUser,
			settings: defaultSettings as Partial<LayoutSettings>,
			languages
		};
	}

	return {
		fetchUserInfo,
		settings: defaultSettings as Partial<LayoutSettings>
	};
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
	return {
		actionsRender: () => [<Question key="doc" />, <SelectLang key="SelectLang" />],
		avatarProps: {
			src: initialState?.currentUser?.avatar,
			icon: <UserOutlined />,
			title: <AvatarName />,
			render: (_, avatarChildren) => {
				return <AvatarDropdown menu>{avatarChildren}</AvatarDropdown>;
			}
		},
		waterMarkProps: {
			content: initialState?.currentUser?.name
		},
		footerRender: () => <Footer />,
		onPageChange: () => {
			const { location } = history;
			// 如果没有登录，重定向到 login
			if (!initialState?.currentUser && location.pathname !== loginPath) {
				history.push(loginPath);
			}
		},
		bgLayoutImgList: [
			{
				src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
				left: 85,
				bottom: 100,
				height: '303px'
			},
			{
				src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
				bottom: -68,
				right: -45,
				height: '303px'
			},
			{
				src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
				bottom: 0,
				left: 0,
				width: '331px'
			}
		],
		links: isDev
			? [
					<Link key="openapi" to="/umi/plugin/openapi" target="_blank">
						<LinkOutlined />
						<span>OpenAPI 文档</span>
					</Link>
				]
			: [],
		menuHeaderRender: undefined,
		// 自定义 403 页面
		// unAccessible: <div>unAccessible</div>,
		// 增加一个 loading 的状态
		childrenRender: (children) => {
			// if (initialState?.loading) return <PageLoading />;
			return (
				<StrictMode>
					{children}
					{isDev && (
						<SettingDrawer
							disableUrlParams
							enableDarkTheme
							settings={initialState?.settings}
							onSettingChange={(settings) => {
								setInitialState((preInitialState) => ({
									...preInitialState,
									settings
								}));
							}}
						/>
					)}
				</StrictMode>
			);
		},
		...initialState?.settings
	};
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
// 请求配置
const runtimeConfig = typeof window !== 'undefined' ? (window as any).__APP_CONFIG__ : undefined;
const runtimeBaseURL = runtimeConfig?.API_URL;
const envBaseURL = process.env.API_URL;
const envForceProxy = process.env.FORCE_PROXY_IN_DEV;
const parseBoolean = (value: unknown) =>
	value === true || value === 'true' || value === 1 || value === '1';
const forceProxyInDev = parseBoolean(runtimeConfig?.FORCE_PROXY_IN_DEV ?? envForceProxy);
const resolveBaseURL = () => {
	const configured = runtimeBaseURL || envBaseURL;
	if (typeof window === 'undefined' || !configured) {
		return configured;
	}
	const isLocalhost =
		window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
	if (isDev && forceProxyInDev) {
		return '/';
	}
	if (!isLocalhost) {
		return configured;
	}
	try {
		const configuredUrl = new URL(configured, window.location.origin);
		if (configuredUrl.origin !== window.location.origin) {
			return '/';
		}
	} catch (_error) {
		return configured;
	}
	return configured;
};

export const request: RequestConfig = {
	baseURL: resolveBaseURL(),
	...errorConfig
};

// 在应用初始化时接入性能与错误监控
// 生产环境压制 console 噪音（保留 error）
if (process.env.NODE_ENV === 'production') {
	const noop = () => {};
	// eslint-disable-next-line no-console
	console.log = noop;
	// eslint-disable-next-line no-console
	console.info = noop;
	// eslint-disable-next-line no-console
	console.debug = noop;
	// eslint-disable-next-line no-console
	console.warn = noop;
}
// 开发环境：增强 warn 输出，定位循环引用相关的告警来源
if (process.env.NODE_ENV === 'development') {
	// 防止重复包裹
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-expect-error
	if (!window.__warnPatched) {
		const originalWarn = console.warn;
		console.warn = (...args: any[]) => {
			try {
				const msg = typeof args[0] === 'string' ? args[0] : '';
				if (msg && /circular/i.test(msg)) {
					originalWarn(...args, new Error('console.warn stack').stack);
				} else {
					originalWarn(...args);
				}
			} catch (_e) {
				originalWarn(...args);
			}
		};
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-expect-error
		window.__warnPatched = true;
	}
}
